import React, { useState } from 'react';
import { Typography, Space, Image, Modal } from 'antd';
import {
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  SwapOutlined,
  UndoOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  PlayCircleOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { Box } from '@mui/material';

interface VehicleMediaProps {
  vehicle: {
    vehicleImage?: { vehicleImageUrl: string }[];
    vehicleVideo?: { vehicleVideoUrl: string }[];
  };
}

const iconStyle = { color: '#fff', fontSize: 18, cursor: 'pointer' };

const VehicleMedia: React.FC<VehicleMediaProps> = ({ vehicle }) => {
  const [previewIndex, setPreviewIndex] = useState(0);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [videoModal, setVideoModal] = useState<{ visible: boolean; url: string | null }>({
    visible: false,
    url: null,
  });

  const imageList = vehicle.vehicleImage ?? [];
  const videoList = vehicle.vehicleVideo ?? [];

  const handleDownload = (url: string) => {
    const suffix = url.slice(url.lastIndexOf('.'));
    const filename = Date.now() + suffix;
    fetch(url)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(blobUrl);
        link.remove();
      });
  };

  return (
    <div style={{ marginTop: 24 }}>
      {/* --- Vehicle Images --- */}
      {imageList.length > 0 && (
        <div>
          <Typography style={{ marginBottom: 8, fontWeight: 'bold', fontSize: 16 }}>
            Vehicle Images
          </Typography>

          <Space wrap>
            {imageList.map((img, idx) => (
              <Image
                key={idx}
                width={100}
                height={70}
                style={{
                  objectFit: 'cover',
                  borderRadius: 4,
                  border: '1px solid #d9d9d9',
                  cursor: 'pointer',
                }}
                src={img.vehicleImageUrl}
                alt={`Vehicle ${idx + 1}`}
                preview={false}
                onClick={() => {
                  setPreviewIndex(idx);
                  setImageModalVisible(true);
                }}
              />
            ))}
          </Space>

          {imageModalVisible && (
            <Image.PreviewGroup
              preview={{
                visible: imageModalVisible,
                current: previewIndex,
                onVisibleChange: (vis) => setImageModalVisible(vis),
                onChange: (index) => setPreviewIndex(index),
                toolbarRender: (
                  _,
                  {
                    transform: { scale },
                    actions: {
                      onActive,
                      onFlipY,
                      onFlipX,
                      onRotateLeft,
                      onRotateRight,
                      onZoomOut,
                      onZoomIn,
                      onReset,
                    },
                  }
                ) => (
                  <div
                    style={{
                      position: 'fixed',
                      top: 24,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'rgba(0,0,0,0.7)',
                      padding: '6px 10px',
                      borderRadius: 6,
                      zIndex: 1601,
                      display: 'flex',
                      gap: 12,
                      alignItems: 'center',
                      backdropFilter: 'blur(4px)',
                    }}
                  >
                    <LeftOutlined style={iconStyle} onClick={() => onActive?.(-1)} />
                    <RightOutlined style={iconStyle} onClick={() => onActive?.(1)} />
                    <DownloadOutlined
                      style={iconStyle}
                      onClick={() =>
                        handleDownload(imageList[previewIndex]?.vehicleImageUrl)
                      }
                    />
                    <SwapOutlined rotate={90} style={iconStyle} onClick={onFlipY} />
                    <SwapOutlined style={iconStyle} onClick={onFlipX} />
                    <RotateLeftOutlined style={iconStyle} onClick={onRotateLeft} />
                    <RotateRightOutlined style={iconStyle} onClick={onRotateRight} />
                    <ZoomOutOutlined style={iconStyle} onClick={onZoomOut} disabled={scale === 1} />
                    <ZoomInOutlined style={iconStyle} onClick={onZoomIn} disabled={scale === 50} />
                    <UndoOutlined style={iconStyle} onClick={onReset} />
                  </div>
                ),
              }}
            >
              {imageList.map((img, idx) => (
                <Image key={idx} src={img.vehicleImageUrl} alt={`Preview ${idx + 1}`} />
              ))}
            </Image.PreviewGroup>
          )}

          {imageModalVisible && (
            <Box
              sx={{
                position: 'fixed',
                top: { xs: 220, sm: 30 },
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0,0,0,0.7)',
                padding: '6px 10px',
                zIndex: 1600,
                display: 'flex',
                borderRadius: 1,
                backdropFilter: 'blur(4px)',
                maxWidth: '90vw',
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  height: 6,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  borderRadius: 2,
                },
              }}
            >
              {imageList.map((img, idx) => (
                <Box
                  key={idx}
                  component="img"
                  src={img.vehicleImageUrl}
                  onClick={() => setPreviewIndex(idx)}
                  loading="lazy" // ✅ this is the lazy load!
                  sx={{
                    width: 58,
                    height: 36,
                    objectFit: 'cover',
                    borderRadius: 1,
                    border:
                      previewIndex === idx
                        ? '2px solid #fff'
                        : '1px solid rgba(255, 255, 255, 0.3)',
                    cursor: 'pointer',
                    opacity: previewIndex === idx ? 1 : 0.6,
                    transition: '0.2s',
                    mr: 1,
                  }}
                />
              ))}
            </Box>
          )}
        </div>
      )}

      {/* --- Vehicle Videos --- */}
      {videoList.length > 0 && (
        <>
          <Typography style={{ margin: '24px 0 8px', fontWeight: 'bold', fontSize: 16 }}>
            Vehicle Video
          </Typography>
          <Space wrap>
            {videoList.map((video, idx) => (
              <div
                key={idx}
                onClick={() => setVideoModal({ visible: true, url: video.vehicleVideoUrl })}
                style={{
                  width: 100,
                  height: 70,
                  background: '#000',
                  position: 'relative',
                  borderRadius: 4,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: '1px solid #d9d9d9',
                }}
              >
                <video
                  src={video.vehicleVideoUrl}
                  muted
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <PlayCircleOutlined
                  style={{
                    fontSize: 24,
                    color: '#fff',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              </div>
            ))}
          </Space>

          {/* Video Modal */}
          {videoModal.visible && videoModal.url && (
            <Modal
              open={videoModal.visible}
              onCancel={() => setVideoModal({ visible: false, url: null })}
              afterClose={() => setVideoModal({ visible: false, url: null })}
              footer={null}
              centered
              closable={false}
              width="auto"
              styles={{
                body: {
                  padding: 0,
                  background: '#000',
                  position: 'relative',
                },
              }}
              maskStyle={{ backgroundColor: 'rgba(0,0,0,0.9)' }}
            >
              <CloseOutlined
                onClick={() => setVideoModal({ visible: false, url: null })}
                style={{
                  position: 'absolute',
                  top: -48,
                  right: -48,
                  fontSize: 24,
                  color: '#fff',
                  cursor: 'pointer',
                  zIndex: 1000,
                }}
              />
              {videoList.length > 1 && (
                <>
                  <LeftOutlined
                    onClick={() => {
                      const current = videoList.findIndex(
                        (v) => v.vehicleVideoUrl === videoModal.url
                      );
                      const prev = (current - 1 + videoList.length) % videoList.length;
                      setVideoModal({ visible: true, url: videoList[prev].vehicleVideoUrl });
                    }}
                    style={{
                      position: 'absolute',
                      left: -48,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: 32,
                      color: '#fff',
                      cursor: 'pointer',
                      zIndex: 1000,
                    }}
                  />
                  <RightOutlined
                    onClick={() => {
                      const current = videoList.findIndex(
                        (v) => v.vehicleVideoUrl === videoModal.url
                      );
                      const next = (current + 1) % videoList.length;
                      setVideoModal({ visible: true, url: videoList[next].vehicleVideoUrl });
                    }}
                    style={{
                      position: 'absolute',
                      right: -48,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: 32,
                      color: '#fff',
                      cursor: 'pointer',
                      zIndex: 1000,
                    }}
                  />
                </>
              )}
              <div style={{ display: 'inline-block' }}>
                <video
                  key={videoModal.url}
                  src={videoModal.url}
                  controls
                  autoPlay
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    display: 'block',
                    backgroundColor: '#000',
                  }}
                />
              </div>
            </Modal>
          )}
        </>
      )}
    </div>
  );
};

export default VehicleMedia;
